import { useEffect, useState } from "react";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import { useCompetitionByType } from "@/app/redux/features/categories/selectors";
import { getSubscriptionLobbyContent } from "@/app/(custom-header)/subscriber-competitions/subscription-competitions-service";

const IMG = "/assets/images/botb-pass/exclusive-draws";

const LivePill = ({ count, className = "" }: { count: number; className?: string }) => (
  <div className={`bg-white rounded-full flex items-center justify-center gap-2 ${className}`}>
    <div className="relative w-3 h-3 shrink-0">
      <div className="absolute inset-0 bg-[rgba(243,52,36,0.38)] rounded-full animate-ping [animation-duration:2s]" />
      <div className="absolute inset-0 m-auto w-2.5 h-2.5 bg-[#F33424] rounded-full" />
    </div>
    <span className="text-xs lg:text-sm font-medium text-dark-1 whitespace-nowrap">{count} Competitions Live Now!</span>
  </div>
);

const StatDivider = ({ className = "" }: { className?: string }) => (
  <div className={`w-px bg-white border border-solid border-white opacity-20 ${className}`} />
);

const ExclusiveDraws = () => {
  const isMobile = useIsMobile();
  const subscriptionCategory = useCompetitionByType("subscriberCompetitions");
  const liveCompetitionCount = subscriptionCategory?.competitions?.length ?? 0;
  const [lobbyContent, setLobbyContent] = useState<SubscriptionLobbyContent | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    getSubscriptionLobbyContent(abortController)
      .then(res => setLobbyContent(res))
      .catch(err => console.error(err));
    return () => abortController.abort();
  }, []);

  return (
    <div className="font-jost mb-8 lg:mb-[76px]">
      <div className="font-bold text-xl lg:text-[32px] text-dark-1 text-center leading-6.5 lg:leading-10 mb-5 lg:mb-8">
        Love winning? You&apos;re in the right place.
      </div>

      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundImage: isMobile
            ? "linear-gradient(-12deg, #BC0B6A 10.9%, #991990 17.7%, #24339F 54.4%, #0B1669 83.6%)"
            : "linear-gradient(95deg, #BC0B6A 1%, #991990 29.7%, #24339F 80.5%, #0B1669 94.7%)",
        }}>
        {isMobile ? (
          <div className="flex flex-col gap-8 items-end px-3 py-4">
            {/* Top Section: badge + title + pill (gap-16) */}
            <div className="flex flex-col gap-4 items-center w-full">
              <img src={`${IMG}/badge-new.webp`} alt="Exclusive Draws For Members" className="w-[250px] h-auto" />
              <div className="flex flex-col gap-2 items-center text-center w-full">
                <div className="font-bold text-xl leading-6.5 text-white">BOTB Pass Competitions</div>
                <div className="font-medium text-sm leading-5 text-white/60">
                  Enjoy complimentary access to exclusive, members competitions
                </div>
              </div>
              <LivePill count={liveCompetitionCount} className="h-7 px-3" />
            </div>

            {/* Bottom Section: stats + highlights (gap-32) */}
            <div className="flex flex-col gap-8 w-full">
              {/* Stat Row (gap-12) */}
              <div className="flex flex-col gap-3 items-center w-full">
                <div className="text-[10px] leading-3 font-medium text-white uppercase">
                  Prizes given in botb pass comps
                </div>
                <div className="flex items-center justify-between min-[400px]:justify-center gap-2 min-[400px]:gap-4 w-full">
                  <div className="flex gap-2 items-center">
                    <img src={`${IMG}/stat-winners.webp`} alt="Winners" className="w-7 h-7 object-cover" />
                    <div>
                      <div className="text-base leading-5 font-bold text-white">{lobbyContent?.totalWinnerCount}</div>
                      <div className="text-xs leading-4.5 font-medium text-white/60">Winners</div>
                    </div>
                  </div>
                  <StatDivider className="h-8" />
                  <div className="flex gap-2 items-center">
                    <img src={`${IMG}/stat-prizes.webp`} alt="Prizes" className="w-7 h-7 object-cover" />
                    <div>
                      <div className="text-base leading-5 font-bold text-white">{lobbyContent?.totalPrizeCount}</div>
                      <div className="text-xs leading-4.5 font-medium text-white/60">Prizes given</div>
                    </div>
                  </div>
                  <StatDivider className="h-8" />
                  <div className="flex gap-2 items-center">
                    <img src={`${IMG}/stat-won.webp`} alt="Won" className="w-7 h-7 object-cover" />
                    <div>
                      <div className="text-base leading-5 font-bold text-white">{lobbyContent?.totalPrizeValue}</div>
                      <div className="text-xs leading-4.5 font-medium text-white/60">Won</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Highlights Section (gap-16) */}
              <div className="flex flex-col gap-4 w-full">
                <div className="w-full h-px bg-white/10" />
                <div className="flex gap-3 items-center">
                  <img
                    src={`${IMG}/highlight-daily.webp`}
                    alt="A New Daily Shot to Win"
                    className="w-[65px] h-[65px] object-cover shrink-0"
                  />
                  <div className="flex flex-col gap-1">
                    <div className="font-bold text-base leading-5.5 text-white">A New Daily Shot to Win</div>
                    <div className="font-medium text-[13px] leading-4.5 text-white/60">
                      Enjoy <span className="text-[#FDD692]">extra prizes</span> every day
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <img
                    src={`${IMG}/highlight-auto.webp`}
                    alt="Automatic Entry, Zero Hassle"
                    className="w-[65px] h-[65px] object-cover shrink-0"
                  />
                  <div className="flex flex-col gap-1">
                    <div className="font-bold text-base leading-5.5 text-white">Automatic Entry, Zero Hassle</div>
                    <div className="font-medium text-[13px] leading-4.5 text-white/60">
                      Effortless, automatic entries
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-11 py-5.5 gap-[39px]">
            {/* Left Column */}
            <div className="flex items-end grow-1">
              <div className="flex flex-col gap-12 pb-3">
                <div className="flex flex-col gap-2">
                  <div className="font-bold text-[28px] leading-8 text-white">
                    BOTB Pass
                    <br />
                    Competitions
                  </div>
                  <div className="font-medium text-lg leading-6 text-white/60 max-w-[333px]">
                    Enjoy complimentary access to exclusive, members competitions
                  </div>
                </div>
                <LivePill count={liveCompetitionCount} className="h-9 w-[220px]" />
              </div>

              {/* Center Column (badge + stats) */}
              <div className="flex flex-col items-end justify-between h-[240px] w-[435px] -mr-4 relative -left-[15px]">
                <img src={`${IMG}/badge-new.webp`} alt="Exclusive Draws For Members" className="w-[345px] h-auto" />
                <div className="w-full">
                  <div className="text-xs leading-3 font-medium text-white uppercase mb-3">
                    Prizes given in botb pass comps
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex gap-3 items-center">
                      <img src={`${IMG}/stat-winners.webp`} alt="Winners" className="w-12 h-12 object-cover" />
                      <div>
                        <div className="text-[20px] leading-5 font-bold text-white">
                          {lobbyContent?.totalWinnerCount}
                        </div>
                        <div className="text-base leading-4.5 font-medium text-white/60">Winners</div>
                      </div>
                    </div>
                    <StatDivider className="h-8" />
                    <div className="flex gap-1.5 items-center">
                      <img src={`${IMG}/stat-prizes.webp`} alt="Prizes" className="w-12 h-12 object-cover" />
                      <div>
                        <div className="text-[20px] leading-5 font-bold text-white">
                          {lobbyContent?.totalPrizeCount}
                        </div>
                        <div className="text-base leading-4.5 font-medium text-white/60">Prizes given</div>
                      </div>
                    </div>
                    <StatDivider className="h-8" />
                    <div className="flex gap-1.5 items-center">
                      <img src={`${IMG}/stat-won.webp`} alt="Won" className="w-12 h-12 object-cover" />
                      <div>
                        <div className="text-[20px] leading-5 font-bold text-white">
                          {lobbyContent?.totalPrizeValue}
                        </div>
                        <div className="text-base leading-4.5 font-medium text-white/60">Won</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider */}
            <div className="w-px h-[221px] bg-white/10 shrink-0" />

            {/* Right Column */}
            <div className="flex flex-col justify-between h-[213px] w-[420px] shrink-0">
              <div className="flex gap-5 items-center">
                <img
                  src={`${IMG}/highlight-daily.webp`}
                  alt="A New Daily Shot to Win"
                  className="w-24 h-24 object-cover shrink-0"
                />
                <div className="flex flex-col gap-1">
                  <div className="font-bold text-[22px] leading-7 text-white">A New Daily Shot to Win</div>
                  <div className="font-medium text-base leading-5.5 text-white/60">
                    Enjoy <span className="text-[#FDD692]">extra prizes</span> every day
                  </div>
                </div>
              </div>
              <div className="flex gap-5 items-center">
                <img
                  src={`${IMG}/highlight-auto.webp`}
                  alt="Automatic Entry, Zero Hassle"
                  className="w-24 h-24 object-cover shrink-0"
                />
                <div className="flex flex-col gap-1">
                  <div className="font-bold text-[22px] leading-7 text-white">Automatic Entry, Zero Hassle</div>
                  <div className="font-medium text-base leading-5.5 text-white/60">Effortless, automatic entries</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 mt-3 lg:mt-4 w-full">
        <div className="shrink-0 w-[18px]">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <g clipPath="url(#clip0_exclusive_info)">
              <path
                d="M9 1.5C13.1423 1.5 16.5 4.85775 16.5 9C16.5016 10.9691 15.7287 12.8598 14.3482 14.264C12.9677 15.6681 11.0904 16.473 9.12152 16.5049C7.15267 16.5369 5.25025 15.7932 3.825 14.4345C2.39974 13.0758 1.56599 11.2111 1.50375 9.243L1.5 9L1.503 8.79C1.614 4.74525 4.9275 1.5 9 1.5ZM9 8.25H8.25L8.16225 8.25525C7.97996 8.27693 7.81196 8.36472 7.69006 8.50197C7.56816 8.63923 7.50084 8.81643 7.50084 9C7.50084 9.18357 7.56816 9.36077 7.69006 9.49803C7.81196 9.63528 7.97996 9.72307 8.16225 9.74475L8.25 9.75V12L8.25525 12.0878C8.27499 12.2552 8.35056 12.411 8.46976 12.5302C8.58896 12.6494 8.74484 12.725 8.91225 12.7448L9 12.75H9.75L9.83775 12.7448C10.0052 12.725 10.161 12.6494 10.2802 12.5302C10.3994 12.411 10.475 12.2552 10.4948 12.0878L10.5 12L10.4948 11.9123C10.4768 11.7592 10.4122 11.6155 10.3097 11.5005C10.2072 11.3855 10.0717 11.3048 9.92175 11.2695L9.83775 11.2545L9.75 11.25V9L9.74475 8.91225C9.72501 8.74484 9.64944 8.58896 9.53024 8.46976C9.41104 8.35056 9.25516 8.27499 9.08775 8.25525L9 8.25ZM9.0075 6L8.91225 6.00525C8.72996 6.02693 8.56196 6.11472 8.44006 6.25197C8.31816 6.38923 8.25084 6.56643 8.25084 6.75C8.25084 6.93357 8.31816 7.11077 8.44006 7.24803C8.56196 7.38528 8.72996 7.47307 8.91225 7.49475L9 7.5L9.09525 7.49475C9.27754 7.47307 9.44554 7.38528 9.56744 7.24803C9.68934 7.11077 9.75666 6.93357 9.75666 6.75C9.75666 6.56643 9.68934 6.38923 9.56744 6.25197C9.44554 6.11472 9.27754 6.02693 9.09525 6.00525L9.0075 6Z"
                fill="#898994"
              />
            </g>
            <defs>
              <clipPath id="clip0_exclusive_info">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <div className="text-xs lg:text-sm leading-4 lg:leading-5 font-normal text-[#696971]">
          New competitions are added regularly as a complimentary bonus to your plan.
        </div>
      </div>
    </div>
  );
};

export default ExclusiveDraws;
